import { fetcher } from '@chipinside/fetcher'
import { Moment } from '@chipinside/frontend'
import { Translate } from '@chipinside/translate'
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Stack,
  styled,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { grey, red } from '@mui/material/colors'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { DetailError } from '#/components/ErrorBoundary'
import { Icon } from '#/components/Icon'
import { usePermissions } from '#/store/ducks/permissions'
import { endpoint } from '#/store/services/endpoints'
import { Sentry } from '#/store/services/Sentry'
import Palette from '#/styles/Palette'
import { isLocal } from '#/utils/processEnvironment'

type UserFeedbackForm = {
  name: string
  email: string
  message: string
}

type UserFeedbackProps = {
  eventId: string
  error: DetailError
  user?: {
    name?: string
    email?: string
  }
}
type SendFeedbackParams = {
  form: UserFeedbackForm
  eventId: string
  error: DetailError
}

const ErrorInfoStack = styled(Stack)({
  backgroundColor: Palette.gray.self[100],
  borderRadius: 12,
  gap: 0.5,
  padding: '8px 16px',
  position: 'relative',
})

const UserFeedbackBox = styled(Box)({
  alignItems: 'center',
  backgroundColor: grey[100],
  display: 'flex',
  justifyContent: 'center',
  minHeight: '100vh',
})

const FeedbackCard = styled(Card)({
  borderRadius: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  margin: 24,
  maxWidth: '100%',
  padding: 24,
  width: 500,
})

const ErrorText = styled(Typography)({
  display: '-webkit-box',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: '1',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
})

function sendFeedback({ error, eventId, form }: SendFeedbackParams) {
  if (error?.status_code === 500 && eventId) {
    return fetcher({
      controller: endpoint.sentry.backend_feedback(),
      method: 'post',
      params: {
        ...form,
        comments: form.message,
        event_id: eventId,
      },
    })
  }

  return new Promise(resolve => {
    Sentry.captureFeedback({
      ...form,
      associatedEventId: eventId,
    })
    setTimeout(() => resolve({ eventId }), 100)
  })
}

function redirectAfterClose() {
  const { pathname } = window.location
  /**
   *  Verifica se a string começa com "/farm"
   */
  if (pathname.startsWith('/farm')) {
    /**
     *  Divide o caminho e substitui pela nova rota, mantendo o slug da fazenda
     */
    const newPath = `/farm/${pathname.split('/')[2]}`
    window.location.replace(newPath)
  } else {
    window.location.replace('/')
  }
}

export function UserFeedback({ error, eventId, user }: UserFeedbackProps) {
  const [loading, setLoading] = useState(false)

  /**
   * Envia feedback automático ao montar o componente
   */
  useEffect(() => {
    Sentry.captureFeedback({
      associatedEventId: eventId,
      email: user?.email,
      message: 'Auto: The sentry screen has been displayed',
      name: user?.name,
      url: window.location.href,
    })
    return () => {}
  }, [eventId])

  const handleSubmit = useCallback(
    (event: ChangeEvent<HTMLFormElement>) => {
      event.preventDefault()
      setLoading(true)

      const formData = new FormData(event.currentTarget)
      const form = Object.fromEntries(formData.entries()) as UserFeedbackForm

      toast.promise(sendFeedback({ error, eventId, form }), {
        duration: 700,
        error: Translate({ messageKey: 'error_sending_feedback' }),
        loading: 'Loading...',
        onAutoClose: () => {
          setLoading(false)
          redirectAfterClose()
        },
        success: Translate({
          messageKey: 'register_item_updated_success_male',
          params: { item: 'feedback' },
        }),
      })
    },
    [eventId, error],
  )

  return (
    <UserFeedbackBox id="userfeedback">
      <FeedbackCard>
        <Header />
        <ErrorBlock error={error} eventId={eventId} />
        <form onSubmit={handleSubmit}>
          <Stack sx={{ gap: 2.5 }}>
            <TextField
              name="name"
              label={Translate({ messageKey: 'user' })}
              value={user?.name}
              disabled
            />
            <TextField
              name="email"
              label={Translate({ messageKey: 'email' })}
              value={user?.email}
              disabled
            />

            <TextField
              name="message"
              multiline
              rows={4}
              required
              autoFocus
              label={Translate({ messageKey: 'help_us_understand_what_happened' })}
              placeholder={Translate({ messageKey: 'report_cause_of_error' })}
            />

            <Stack
              direction="row"
              sx={{
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                sx={{ flexGrow: { sm: 'unset', xs: 1 }, px: 4 }}
                color="inherit"
                onClick={redirectAfterClose}
              >
                {Translate({ messageKey: 'close' })}
              </Button>

              <Button
                variant="contained"
                startIcon={
                  loading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <Icon size={18} name="send" />
                  )
                }
                sx={{ backgroundColor: grey[900], flexGrow: 1 }}
                type="submit"
                disabled={loading}
              >
                {Translate({ messageKey: loading ? 'sending' : 'submit_feedback' })}
              </Button>
            </Stack>
          </Stack>
        </form>

        <Typography
          variant="caption"
          sx={{
            color: 'grey.700',
            textAlign: 'center',
          }}
        >
          {Translate({ messageKey: 'this_report_helps_our_team_fix_the_problem' })}
        </Typography>

        <Copyright sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column' }} />
      </FeedbackCard>
    </UserFeedbackBox>
  )
}

const Header = () => (
  <Stack
    direction="row"
    sx={{
      alignItems: 'center',
      gap: 2,
    }}
  >
    <Avatar sx={{ backgroundColor: red[50] }}>
      <Icon size="large" name="triangle-alert" color={red[600]} />
    </Avatar>
    <Box>
      <Typography
        color="error"
        sx={{
          fontWeight: 500,
        }}
      >
        {Translate({ messageKey: '500_fallback_title' })}
      </Typography>
      <Typography variant="body2">
        {Translate({ messageKey: 'help_us_understand_what_happened' })}
      </Typography>
    </Box>
  </Stack>
)

const Copyright = props => {
  const theme = useTheme()

  return (
    <Box className="noselect" {...props}>
      <Typography sx={{ color: theme.palette.grey[500], fontSize: 12 }}>Powered by</Typography>
      <a href="https://www.cowmed.com.br/" target="_blank" rel="noopener noreferrer">
        <svg
          viewBox="0 0 93 13"
          style={{ fill: theme.palette.grey[600], maxWidth: 110, width: 110 }}
        >
          <path d="M56.5892 6.90535L50.1126 0.15686H49.0332V12.3911H50.2665V1.90571L56.1123 7.95466H57.0482L62.9043 1.92371V12.3911H64.1581V0.15686H63.0479L56.5892 6.90535Z" />
          <path d="M67.7866 6.54013H76.199V5.5474H67.7866V1.13928H77.64L77.4323 0.161987H66.4688V12.3911H77.5759L77.7912 11.4112H67.7866V6.54013Z" />
          <path d="M85.1931 0.161987H79.5703V2.22203H80.8856V1.14186H85.1931C89.4365 1.14186 91.4364 2.78783 91.4364 6.27781C91.4364 9.76779 89.439 11.4138 85.1931 11.4138H80.8856V9.86809H79.5703V12.3936H85.1931C90.3364 12.3936 92.7722 10.4339 92.7722 6.28038C92.7722 2.12687 90.3364 0.167131 85.1931 0.167131" />
          <path d="M80.8801 8.55908V6.62506H82.7364V5.46002H80.8801V3.526H79.5751V5.46002H77.7188V6.62506H79.5751V8.55908H80.8801Z" />
          <path d="M42.9285 10.7888H42.467L39.0185 0.151738H35.8648L32.3727 10.7888H31.9137L29.7292 0.151738H27.3319L30.2369 12.3911H33.9341L37.4468 1.93659L40.8953 12.3911H44.5823L47.4975 0.151738H45.1592L42.926 10.7888H42.9285ZM19.699 10.8248C16.0094 10.8248 14.348 9.42834 14.348 6.2727C14.348 3.11706 16.0094 1.73084 19.699 1.73084C21.5296 1.73084 22.8809 2.0909 23.7347 2.81873C24.5859 3.54141 25.0115 4.69874 25.0115 6.27527C25.0115 9.43091 23.368 10.8274 19.699 10.8274M19.699 12.548C24.8756 12.548 27.3498 10.5342 27.3498 6.2727C27.3498 2.01117 24.8756 0 19.699 0C14.5223 0 12.0071 2.01117 12.0071 6.2727C12.0071 10.5342 14.4813 12.548 19.699 12.548ZM11.9712 12.3937L12.2891 10.7323H7.72523C4.05363 10.7323 2.33578 9.30746 2.33578 6.27527C2.33578 3.24308 4.05363 1.81571 7.72523 1.81571H12.2891L11.9712 0.15431H7.72523C2.46141 0.15431 0 2.10376 0 6.27527C0 10.4468 2.45884 12.3937 7.72523 12.3937H11.9712Z" />
        </svg>
      </a>
    </Box>
  )
}

const ErrorBlock = ({ error, eventId }: { error: DetailError; eventId: string }) => {
  const file = (error as any)?.file?.split('/').pop()
  const permissions = usePermissions()

  const errorDetails = (
    <>
      {error && <ErrorText variant="caption">{String((error as any)?.message ?? error)}</ErrorText>}
      {file && <ErrorText variant="caption">File: {file}</ErrorText>}
    </>
  )

  const ErrorInfoLocalProps = isLocal
    ? {
        onClick: () => {
          const infos = {
            code: error.status_code,
            error: (error as any)?.message,
            eventId,
            file: (error as any)?.file,
            url: window.location.pathname,
          }
          navigator.clipboard.writeText(JSON.stringify(infos, null, 2))
        },
        sx: {
          '&:hover': { backgroundColor: grey[200], cursor: 'pointer' },
        },
      }
    : {}

  return (
    <ErrorInfoStack {...ErrorInfoLocalProps}>
      {permissions.system.access_telescope && errorDetails}
      {eventId && <Typography variant="caption">ID: {eventId}</Typography>}
      <Typography variant="caption">URL: {window.location.pathname}</Typography>
      <Typography variant="caption">
        {Translate({ messageKey: 'date' })}: {Moment().format('L LT')}
      </Typography>

      {error?.status_code && (
        <Chip
          label={error.status_code}
          color="error"
          size="small"
          sx={{ bottom: 10, position: 'absolute', right: 10 }}
        />
      )}
    </ErrorInfoStack>
  )
}
